import React, { useEffect, useState } from "react";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Link, useNavigate } from "react-router-dom";
import { RiskNote } from "../components/layout/RiskDisclaimer";
import { ArrowRight, ShieldCheck, Clock, Target, Boxes } from "lucide-react";

export default function Invest() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [spvs, setSpvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    api.get("/spvs")
      .then((r) => {
        if (cancelled) return;
        setSpvs(Array.isArray(r.data) ? r.data : []);
        setError(false);
      })
      .catch(() => {
        if (cancelled) return;
        setSpvs([]);
        setError(true);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handleInvest = (spv_id) => {
    if (!user) return navigate("/register?next=/invest");
    if (user.kyc_status !== "qualified") return navigate("/qualification");
    navigate(`/checkout/${spv_id}`);
  };

  return (
    <div>
      <section className="section border-b border-slate-200">
        <div className="container-page">
          <div className="label-divider mb-6">{t.nav.invest}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900" data-testid="invest-title">
            {t.invest.title}
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl">{t.invest.subtitle}</p>
          <div className="mt-8"><RiskNote /></div>
        </div>
      </section>

      {/* Scenarios */}
      <section className="section">
        <div className="container-page">
          <div className="label-divider mb-6">{t.invest.scenarios_title}</div>
          <div className="grid md:grid-cols-3 gap-6">
            {t.invest.scenarios.map((s, i) => (
              <div
                key={s.key}
                className={`bg-white border rounded-sm p-8 ${s.key === "central" ? "border-slate-900 shadow-md" : "border-slate-200"}`}
                data-testid={`scenario-${s.key}`}
              >
                {s.key === "central" && (
                  <div className="text-[10px] uppercase tracking-[0.2em] bg-emerald-700 text-white inline-block px-2 py-1 rounded-sm mb-4">
                    {lang === "fr" ? "Scénario de référence" : "Reference scenario"}
                  </div>
                )}
                <div className="font-display font-semibold text-slate-900 text-lg">{s.name}</div>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="font-mono text-5xl font-semibold text-slate-900">{s.yield}</span>
                  <span className="text-sm text-slate-500">/ {lang === "fr" ? "an cible" : "y target"}</span>
                </div>
                <p className="mt-5 text-sm text-slate-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SPVs list */}
      <section className="section bg-slate-50 border-t border-slate-200">
        <div className="container-page">
          <div className="label-divider mb-6">{lang === "fr" ? "SPV actuellement ouverts" : "Currently open SPVs"}</div>
          {loading && (
            <div className="py-10 text-center text-sm text-slate-500" data-testid="spvs-loading">
              {lang === "fr" ? "Chargement des SPV…" : "Loading SPVs…"}
            </div>
          )}
          {!loading && spvs.length === 0 && (
            <div className="py-12 text-center bg-white border border-slate-200 rounded-sm" data-testid="spvs-empty">
              <div className="text-sm text-slate-700 font-medium">
                {error
                  ? (lang === "fr" ? "Impossible de charger les SPV. Vérifiez votre connexion et réessayez." : "Unable to load SPVs. Check your connection and retry.")
                  : (lang === "fr" ? "Aucun SPV ouvert à la souscription actuellement." : "No SPV currently open for subscription.")}
              </div>
              {error && (
                <Button onClick={() => window.location.reload()} className="rounded-sm bg-slate-900 hover:bg-slate-800 mt-4" data-testid="spvs-retry">
                  {lang === "fr" ? "Réessayer" : "Retry"}
                </Button>
              )}
            </div>
          )}
          <div className="grid lg:grid-cols-2 gap-6">
            {[...spvs]
              .sort((a, b) => {
                // open first, funded after
                if (a.status === b.status) return 0;
                if (a.status === "funded") return 1;
                if (b.status === "funded") return -1;
                return 0;
              })
              .map((spv) => {
              const pct = Math.min(100, Math.round((spv.raised / spv.total_raise) * 100));
              const isFunded = spv.status === "funded" || pct >= 100;
              return (
                <div key={spv.spv_id} className={`bg-white border rounded-sm p-8 transition-all ${isFunded ? "border-slate-200 opacity-90" : "border-slate-200"}`} data-testid={`spv-card-${spv.spv_id}`}>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="text-[10px] uppercase tracking-[0.2em] border border-slate-300 px-2 py-0.5 rounded-sm text-slate-600">
                      {spv.industry}
                    </div>
                    {isFunded ? (
                      <div className="text-[10px] uppercase tracking-[0.2em] bg-slate-900 text-white font-bold px-2 py-0.5 rounded-sm" data-testid={`spv-status-${spv.spv_id}`}>
                        {lang === "fr" ? "Financé" : "Funded"}
                      </div>
                    ) : (
                      <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-bold" data-testid={`spv-status-${spv.spv_id}`}>
                        {lang === "fr" ? "Ouvert" : "Open"}
                      </div>
                    )}
                  </div>
                  <h3 className="font-display text-xl font-semibold text-slate-900">{spv.name}</h3>
                  <p className="text-sm text-slate-500 mt-1">{spv.hardware}</p>
                  <p className="mt-4 text-sm text-slate-600 leading-relaxed">{spv.description}</p>

                  <div className="mt-6 grid grid-cols-3 gap-4 border-t border-slate-200 pt-5">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">{t.invest.target}</div>
                      <div className="font-mono text-lg font-semibold text-slate-900">{spv.target_yield}%</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">{t.invest.horizon}</div>
                      <div className="font-mono text-lg font-semibold text-slate-900">{spv.horizon_months}m</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">{t.invest.min}</div>
                      <div className="font-mono text-lg font-semibold text-slate-900">{spv.min_ticket.toLocaleString()}€</div>
                    </div>
                  </div>

                  <div className="mt-6">
                    <div className="flex justify-between text-xs text-slate-500 mb-1.5">
                      <span>{t.invest.raised}</span>
                      <span className="font-mono text-slate-900">{Math.round(spv.raised).toLocaleString()}€ / {spv.total_raise.toLocaleString()}€</span>
                    </div>
                    <Progress
                      value={pct}
                      className={`h-1.5 rounded-sm transition-[width] duration-700 ${isFunded ? "[&>*]:bg-slate-900" : "[&>*]:bg-emerald-700"}`}
                      data-testid={`spv-progress-${spv.spv_id}`}
                    />
                    <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mt-1">{pct}%</div>
                  </div>

                  <div className="mt-7 flex gap-3">
                    {isFunded ? (
                      <Button
                        disabled
                        className="rounded-sm bg-slate-200 text-slate-500 hover:bg-slate-200 cursor-not-allowed"
                        data-testid={`spv-funded-cta-${spv.spv_id}`}
                      >
                        {lang === "fr" ? "Levée clôturée" : "Round closed"}
                      </Button>
                    ) : (
                      <Button onClick={() => handleInvest(spv.spv_id)} className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid={`invest-btn-${spv.spv_id}`}>
                        {t.invest.invest_cta} <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    )}
                    <Button asChild variant="outline" className="rounded-sm" data-testid={`simulate-btn-${spv.spv_id}`}>
                      <Link to={`/simulator?spv=${spv.spv_id}`}>{t.hero.cta_primary}</Link>
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
